import * as React from "react";
import {
  Grid,
  Box,
  List,
  ListItem,
  ListItemText,
  FormControl,
  Typography,
} from "@mui/material";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import { useContext, useEffect, useState } from "react";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useLocation, useNavigate } from "react-router-dom";
import { PreferencesContext } from "../../PreferenceContext";
import { GetFinancialYearList } from "../../services/MasterService";
import { AlertType } from "../../services/models/AlertType";
import { PAGE_SIZE } from "../../services/common/const";
import { GetDoctorList } from "../../services/DoctorService";
import { Controller, useForm } from "react-hook-form";
import SkeletonWrapper from "../../components/Skeleton/SkeletonWrapper";
import { CustomAsyncSelect } from "../../components/CustomFields/CustomSelect";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";
import { GetLocationList } from "../../services/LocationService";
import {
  GetTeamMemberList,
  GetTeamMemberLocationList,
} from "../../services/TeamMemberService";
import { GetSaveUserOrgBranchSelection } from "../../services/UserService";
import { components } from "react-select";

interface Parameter {
  consumerId: string;
  orgId: string;
  branchId: string;
  pageIndex: number;
  pageSize: number;
  sortColumn: string;
  sortDirection: string;
  searchValue: string;
  tabType: number;
  mode: string;
}

// DEFAULT PARAMS -- ADD HERE IF YOU NEED NEW PARAMETER
const parameters: Parameter = {
  consumerId: "",
  orgId: "",
  branchId: "",
  pageIndex: 1,
  pageSize: PAGE_SIZE,
  sortColumn: "",
  sortDirection: "",
  searchValue: "",
  tabType: 0,
  mode: "DROPDOWN",
};
interface selectOption {
  label: string;
  value: string;
}

interface LocationOption extends selectOption {
  fullAddressWithPostal: string;
}

interface formInitialValueTypes {
  consumerId: number;
  orgId: number;
  branchId: number;
  locationId: LocationOption | null;
  locationName: string;
  locationCode: string;
}

export default function LocationList(props: any) {
  let navigate = useNavigate();
  const { pathname } = useLocation();
  const [isLoading, setIsLoading] = React.useState(false);

  const { userContext, updateUserContext } = useContext(PreferencesContext);
  const [param, setParam] = useState(parameters);
  const [consultingDoctorColl, setConsultingDoctorColl] = useState<any[]>([]);
  const [locationData, setLocationData] = useState<any[]>([]);

  // DEFAULT SCHEMA VALUE FOR FORM
  const initLocationData: formInitialValueTypes = {
    consumerId: 0,
    orgId: 0,
    branchId: 0,
    locationId: userContext?.locationId
      ? {
          label: userContext?.locationName,
          value: userContext?.locationId?.toString(),
          fullAddressWithPostal: userContext?.locationAddress || "",
        }
      : null,
    locationName: userContext?.locationName || "",
    locationCode: userContext?.locationTypeCode || "",
  };

  const validationSchema = Yup.object().shape({
    branchId: Yup.number().nullable(),
    orgId: Yup.number().required(),
    consumerId: Yup.number().required(),
    locationId: Yup.object()
      .shape({
        value: Yup.string(),
        label: Yup.string(),
      })
      .required("Location is required"),
    locationName: Yup.string().nullable(),
    locationCode: Yup.string().nullable(),
  });

  const methods = useForm({
    resolver: yupResolver(validationSchema),
    defaultValues: initLocationData,
  });

  async function handleListItemLocationClick(item: any) {
    const selectedLocationId = item.value;

    updateUserContext({
      ...userContext,
      locationId: selectedLocationId,
      locationName: item.locationName1,
      locationTypeCode: item.locationTypeCode,
      locationAddress: item.fullAddressWithPostal,
      locationCity: item.city,
      isAlert: true,
      alertMessage: `Location changed to ${item.locationName}`,
      alertType: AlertType.Success,
    });
    try {
      const params = new URLSearchParams();
      params.append("orgId", userContext?.organizationId?.toString());
      params.append("userId", userContext?.loginDetail?.userId?.toString());
      params.append("consumerId", userContext?.consumerId?.toString());
      params.append("locationId", selectedLocationId?.toString());

      const result: any = await GetSaveUserOrgBranchSelection(params);
      if (pathname === "/dashboard") {
        navigate(0);
      } else {
        navigate("/dashboard");
      }
    } catch (error) {
      console.error("Location selection failed", error);
    }
  }

  async function fetchLocation(searchValue: string, oninit: boolean) {
    try {
      let params = new URLSearchParams();
      let locationCollection = [];
      params.append("consumerId", userContext.consumerId?.toString());
      params.append("organizationId", userContext.organizationId?.toString());
      // params.append("teamMemberId", doctorId?.toString());
      params.append("teamMemberId", userContext.doctorId?.toString());
      params.append("isContainBranch", "true");
      params.append("pageIndex", param.pageIndex?.toString());
      params.append("pageSize", param.pageSize?.toString());
      params.append("sortColumn", param.sortColumn);
      params.append("sortDirection", param.sortDirection);
      params.append("searchValue", searchValue);
      const response: any = await GetTeamMemberLocationList(params);
      if (response?.data?.data?.locationColl) {
        locationCollection = response.data.data.locationColl;
        setLocationData(locationCollection);

        if (oninit) {
          const defaultLocation = locationCollection.find(
            (x: any) => x.locationId == userContext?.locationId,
          );
          if (defaultLocation) {
            methods.setValue("locationId", {
              value: defaultLocation.locationId,
              label: defaultLocation.locationName1,
              fullAddressWithPostal: defaultLocation.fullAddressWithPostal,
            });
            methods.setValue("locationName", defaultLocation.locationName);
            methods.setValue("locationCode", defaultLocation.code);
          }
        }
      }
      return locationCollection;
    } catch (e: any) {
      updateUserContext({
        ...userContext,
        isAlert: true,
        alertMessage:
          typeof e === "string" ? (e as string) : (e?.message as string),
        alertType: AlertType.Error,
      });
    }
  }

  const locationOptionComponent = (props: any) => {
    return (
      <components.Option {...props}>
        {props.data.__isNew__ && <Box>{props.children}</Box>}

        {props.data && !props.data.__isNew__ && (
          <>
            <Box>{props.children}</Box>

            {props.data?.fullAddressWithPostal && (
              <Box className="font-size-12 font-color-grey-600">
                {props.data?.fullAddressWithPostal}
              </Box>
            )}
          </>
        )}
      </components.Option>
    );
  };

  const locationSingleValueComponent = (props: any) => {
    return (
      <components.SingleValue {...props}>
        <Box>{props.children}</Box>
        {props.data?.fullAddressWithPostal && (
          <Box className="font-size-12 font-color-grey-600">
            {props.data?.fullAddressWithPostal}
          </Box>
        )}
      </components.SingleValue>
    );
  };

  const fetchData = async () => {
    try {
      await fetchLocation("", true);
      setIsLoading(false);
    } catch (e) {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    setIsLoading(true);
    fetchData();
  }, []);

  return (
    <>
      <React.Fragment>
        <Grid>
          <Box>
            <Grid item xs={12} sx={{ paddingTop: "10px" }}>
              <FormControl fullWidth>
                <SkeletonWrapper isLoading={isLoading}>
                  <Controller
                    control={methods.control}
                    name="locationId"
                    render={({ field }) => (
                      <CustomAsyncSelect
                        {...field}
                        isNotCreatable={true}
                        id="locationId"
                        FieldName="locationId"
                        placeholder="Search Location"
                        defaultOptions={locationData}
                        openMenuOnFocus
                        // isClearable
                        isSearchable
                        // isDisabled={isEditDisabled}
                        menuPortalTarget={document.querySelector("body")}
                        value={field.value}
                        handleChange={(e: any) => {
                          methods.setValue("locationId", e?.value ? e : null, {
                            shouldValidate: true,
                          });

                          methods.setValue(
                            "locationName",
                            e?.value ? e.label : null,
                            {
                              shouldValidate: true,
                            },
                          );
                          methods.setValue("locationCode", e?.code || "");

                          handleListItemLocationClick(e);
                        }}
                        CustomOptionComponent={locationOptionComponent}
                        CustomSingleValueComponent={
                          locationSingleValueComponent
                        }
                      />
                    )}
                  />
                </SkeletonWrapper>
                <Typography className="validation-message">
                  {methods.formState.errors.consultingDoctorId?.message}
                </Typography>
              </FormControl>
            </Grid>
          </Box>
        </Grid>
      </React.Fragment>
    </>
  );
}
